import { Box, styled } from '@mui/material';
import { ArrowDown, ArrowUp } from 'lucide-react';
import React from 'react';

import KpiCard from 'components/charts/kpi-card';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import { ChartPalette } from 'components/charts/types/chart-palette.type';
import SparklineChart from 'components/charts/sparkline-chart';

type Delta = {
  value: string;
  direction: 'up' | 'down';
  sentiment: 'good' | 'bad' | 'neutral';
};

type Props = {
  label: string;
  value: string; // pre-formatted by the caller
  delta?: Delta;
  trend?: number[];
};

const DeltaRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(3),
  minWidth: 0,
  marginTop: 'auto',
}));

const DeltaLabel = styled(Box, { shouldForwardProp: (prop) => prop !== 'deltaColor' })<{ deltaColor: string }>(
  ({ theme, deltaColor }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: '0 1 auto',
    minWidth: 0,
    overflowWrap: 'anywhere',
    fontSize: 13,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
    color: deltaColor,
  })
);

const sentimentColor = (palette: ChartPalette, sentiment: Delta['sentiment']): string => {
  if (sentiment === 'good') return palette.status.green;
  if (sentiment === 'bad') return palette.status.red;
  return palette.text.secondary;
};

// `sentiment` is separate from `direction`: mortality trending up is bad, growth trending up is
// good, so sentiment is never inferred from the arrow.
const DataLabKpiCard: React.FC<Props> = ({ label, value, delta, trend }) => {
  const palette = useChartPalette();
  const DeltaIcon = delta?.direction === 'up' ? ArrowUp : ArrowDown;

  return (
    <KpiCard label={label} value={value}>
      <DeltaRow>
        {delta ? (
          <DeltaLabel deltaColor={sentimentColor(palette, delta.sentiment)}>
            <DeltaIcon size={14} />
            {delta.value}
          </DeltaLabel>
        ) : (
          <Box />
        )}
        {trend && trend.length > 0 && (
          <Box flex="1 1 0%" minWidth={0}>
            <SparklineChart data={trend} height={28} disableKeyboardNavigation />
          </Box>
        )}
      </DeltaRow>
    </KpiCard>
  );
};

export default DataLabKpiCard;
